import { type ReactNode, useState } from 'react';

import WarningIcon from '@/assets/svg/bxs-warning.svg?react';
import { Button } from '@/components/buttons';
import {
  Popover,
  PopoverContent,
  type PopoverContentProps,
  PopoverTrigger,
} from '@/components/Popover';
import { Text } from '@/components/typography';

type Props = {
  title: ReactNode;
  body: ReactNode;
  size?: 'small' | 'medium';
  trigger?: 'hover' | 'focus' | 'click' | 'contextMenu';
  placement?: 'top' | 'right' | 'bottom' | 'left';
};

const getPopoverPlacement = (
  placement: NonNullable<Props['placement']>,
): Pick<PopoverContentProps, 'side' | 'align'> => {
  if (placement === 'top') {
    return { side: 'top', align: 'center' };
  }

  if (placement === 'bottom') {
    return { side: 'bottom', align: 'center' };
  }

  if (placement === 'left') {
    return { side: 'left', align: 'center' };
  }

  return { side: 'right', align: 'center' };
};

const WarningPopover = ({
  title,
  body,
  size = 'small',
  trigger = 'click',
  placement = 'right',
}: Props) => {
  const popoverPlacement = getPopoverPlacement(placement);
  const isContextMenuOnlyTrigger = trigger === 'contextMenu';
  const [open, setOpen] = useState(false);

  return (
    <Popover
      modal={false}
      open={isContextMenuOnlyTrigger ? open : undefined}
      onOpenChange={isContextMenuOnlyTrigger ? setOpen : undefined}
    >
      <PopoverTrigger
        openOnHover={trigger === 'hover'}
        render={
          <Button
            variant="ghost"
            className="text-yellow-500!"
            size={size === 'small' ? 'icon-small' : 'icon'}
            onContextMenu={(event) => {
              if (!isContextMenuOnlyTrigger) {
                return;
              }
              event.preventDefault();
              setOpen(true);
            }}
          >
            <WarningIcon />
          </Button>
        }
      />
      <PopoverContent
        side={popoverPlacement.side}
        align={popoverPlacement.align}
        className="dark max-w-60 p-5"
      >
        <div className="flex flex-col gap-2">
          <Text variant="caption1" color="light-green">
            {title}
          </Text>
          <Text variant="body2" color="white">
            {body}
          </Text>
        </div>
      </PopoverContent>
    </Popover>
  );
};

export default WarningPopover;
